import { Component, OnInit, OnDestroy } from '@angular/core'
import { collection, getDocs, orderBy, query, where, Query, DocumentData } from '@angular/fire/firestore'
import { FormControl, FormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { AdminClient, Campaign, CampaignSource, FirestoreStructure, Subscription, ContractStatus, subscriptionsPath, Invoice, InvoiceStatus, parkingsPath, ParkingStatus } from '@parkupp/core'

const PAGE_VIEWS_COLLECTION = 'pageViews'

/** Subscriptions that never actually started – excluded from all tracking calculations */
const EXCLUDED_STATUSES_FROM_TRACKING = [ContractStatus.merchantRejected, ContractStatus.clientCancelled, ContractStatus.paymentFailed]
import { AdminService } from 'src/app/services/admin.service'
import { GlobalService } from 'src/app/services/global.service'
import { CampaignService } from 'src/app/services/campaign.service'
import { EnterpriseContextService } from 'src/app/services/enterprise-context.service'
import { Subscription as RxSubscription } from 'rxjs'

@Component({
    selector: 'app-admin-reporting',
    templateUrl: './admin-reporting.component.html',
    styleUrls: ['./admin-reporting.component.scss'],
})
export class AdminReportingComponent implements OnInit, OnDestroy {
    private componentSubscriptions: RxSubscription[] = []
    private currentLoadedEnterpriseKey: string | null | undefined = undefined

    filterForm: FormGroup = new FormGroup({
        filterDateRange: new FormControl('last30days'),
        customStartDate: new FormControl(''),
        customEndDate: new FormControl(''),
        filterSource: new FormControl(''),
        filterCampaign: new FormControl(''),
    })

    campaignSources = ['', ...Object.values(CampaignSource)] // '' for "Any"
    campaigns: Campaign[] = []
    filteredCampaigns: Campaign[] = []

    dateRangeOptions = [
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'last3months', label: 'Last 3 months' },
        { value: 'last6months', label: 'Last 6 months' },
        { value: 'thisyear', label: 'This year' },
        { value: 'last12months', label: 'Last 12 months' },
        { value: 'alltime', label: 'All time' },
        { value: 'custom', label: 'Custom range' },
    ]

    totalUsers: number = 0
    usersWithProfile: number = 0
    parkers: number = 0

    // Traffic metrics (from page views)
    pageViews: number = 0
    uniqueVisitors: number = 0

    // Subscription metrics
    totalSubscriptions: number = 0
    newSubscriptions: number = 0
    activeSubscriptions: number = 0
    completedSubscriptions: number = 0
    cancelledSubscriptions: number = 0
    breachedSubscriptions: number = 0
    rejectedSubscriptions: number = 0
    expiredSubscriptions: number = 0
    totalBaysBooked: number = 0
    newSubscriptionsBays: number = 0
    totalContractValue: number = 0
    newSubscriptionsTotalContractValue: number = 0
    newSubscriptionsMonthlyFees: number = 0
    newSubscriptionsMonths: number = 0
    totalMonthlyFees: number = 0
    totalMonths: number = 0
    averageSubscriptionValue: number = 0
    averageBaysPerSubscription: number = 0
    averageMonthlyBayPrice: number = 0
    averageMonthsDuration: number = 0

    // Invoice metrics
    totalInvoicesAmount: number = 0
    totalInvoicesPaid: number = 0

    // Payout metrics
    totalPaidOutAmount: number = 0

    // All-time metrics
    allTimeOverdueInvoices: number = 0
    allTimeOverdueAmount: number = 0
    allTimeNotPaidOut: number = 0
    allTimeNotPaidOutAmount: number = 0
    allTimeActiveBuildings: number = 0
    allTimeActiveParkingBays: number = 0
    allTimeTotalSubscriptions: number = 0
    allTimeTotalUsers: number = 0

    // Time-based parking metrics
    newParkingBuildings: number = 0
    newParkingBays: number = 0

    // Current metrics (live status)
    currentActiveSubscriptions: number = 0
    currentActiveBays: number = 0
    currentMonthlyRevenue: number = 0
    currentDraftSubscriptions: number = 0
    currentPendingConfirmationSubscriptions: number = 0

    // Modal state
    showMoreDataModal: boolean = false

    loading: boolean = false
    
    // Tab-based loading for optimization
    activeMetricsTab: 'current' | 'alltime' = 'current'
    loadingCurrentMetrics: boolean = false
    loadingAllTimeMetrics: boolean = false
    private currentMetricsLoaded: boolean = false
    private allTimeMetricsLoaded: boolean = false

    constructor(
        private adminSvc: AdminService,
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private router: Router,
        private campaignService: CampaignService,
        public enterpriseContext: EnterpriseContextService
    ) {}

    /**
     * Get the enterprise key for filtering.
     * All enterprise keys (including 'parkupp') filter by denormEnterpriseKey.
     * This is consistent with how admin-dashboard-page.component.ts works.
     */
    private getFilterEnterpriseKey(): string | null {
        return this.enterpriseContext.currentEnterpriseKey || null
    }

    ngOnInit(): void {
        // Subscribe to enterprise changes
        const entSub = this.enterpriseContext.currentEnterpriseKey$.subscribe((key) => {
            if (this.currentLoadedEnterpriseKey !== key) {
                this.currentLoadedEnterpriseKey = key
                this.loadAllData()
            }
        })
        this.componentSubscriptions.push(entSub)

        // Load campaigns - skip for Enterprise Managers (they don't have access to global campaigns)
        if (!this.enterpriseContext.isEnterpriseManager) {
            this.campaignService.getAll().subscribe((campaigns) => {
                this.campaigns = campaigns
                this.filteredCampaigns = campaigns

                // Check for query params after campaigns are loaded
                const params = this.route.snapshot.queryParams
                if (params['source']) {
                    this.filterForm.patchValue({ filterSource: params['source'] }, { emitEvent: false })
                    if (params['source']) {
                        this.filteredCampaigns = this.campaigns.filter((c) => c.source === params['source'])
                    }
                }
                if (params['campaign']) {
                    this.filterForm.patchValue({ filterCampaign: params['campaign'] }, { emitEvent: false })
                }
                if (params['dateRange']) {
                    this.filterForm.patchValue({ filterDateRange: params['dateRange'] }, { emitEvent: false })
                }
                if (params['source'] || params['campaign'] || params['dateRange']) {
                    this.loadAllData()
                }
            })
        } else {
            // For Enterprise Managers, just check for dateRange query param
            const params = this.route.snapshot.queryParams
            if (params['dateRange']) {
                this.filterForm.patchValue({ filterDateRange: params['dateRange'] }, { emitEvent: false })
                this.loadAllData()
            }
        }

        // Watch for campaign selection to auto-update source
        this.filterForm.get('filterCampaign')?.valueChanges.subscribe((campaignName) => {
            if (campaignName) {
                const selectedCampaign = this.campaigns.find((c) => c.campaignName === campaignName)
                if (selectedCampaign) {
                    this.filterForm.patchValue({ filterSource: selectedCampaign.source }, { emitEvent: false })
                }
            }
        })

        // Watch for source changes to filter campaigns dropdown
        this.filterForm.get('filterSource')?.valueChanges.subscribe((source) => {
            if (source) {
                this.filteredCampaigns = this.campaigns.filter((c) => c.source === source)
            } else {
                this.filteredCampaigns = this.campaigns
            }
        })

        // Watch for filter changes
        this.filterForm.valueChanges.subscribe(() => {
            this.updateUrlParams()
            this.loadAllData()
        })
    }

    ngOnDestroy(): void {
        console.log('[Reporting] ngOnDestroy - cleaning up subscriptions')
        this.componentSubscriptions.forEach((sub) => sub.unsubscribe())
    }

    /**
     * Build query with optional enterprise filter
     */
    private buildQuery(baseCollection: string, ...constraints: any[]): Query<DocumentData> {
        const enterpriseKey = this.getFilterEnterpriseKey()
        let q = query(collection(this.adminSvc.firestore, baseCollection), ...constraints)

        if (enterpriseKey) {
            q = query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }

        return q
    }

    async loadAllData(): Promise<void> {
        this.loading = true
        
        // Reset tab loading states when enterprise changes
        this.currentMetricsLoaded = false
        this.allTimeMetricsLoaded = false

        try {
            // Load primary metrics only - Current/All-Time are loaded via tabs
            await Promise.all([
                this.loadReportingData(),
                this.loadSubscriptionMetrics(),
                this.loadInvoiceMetrics(),
                this.loadPageViewMetrics(),
                this.loadParkingMetrics(),
            ])
            
            // Load the active tab's data
            this.loadActiveTabData()
        } catch (error) {
            console.error('Error loading data:', error)
        } finally {
            this.loading = false
        }
    }
    
    /**
     * Switch metrics tab and load data if not already loaded
     */
    selectMetricsTab(tab: 'current' | 'alltime'): void {
        this.activeMetricsTab = tab
        this.loadActiveTabData()
    }
    
    /**
     * Load data for the active metrics tab
     */
    private loadActiveTabData(): void {
        if (this.activeMetricsTab === 'current' && !this.currentMetricsLoaded) {
            this.loadCurrentMetricsTab()
        } else if (this.activeMetricsTab === 'alltime' && !this.allTimeMetricsLoaded) {
            this.loadAllTimeMetricsTab()
        }
    }
    
    /**
     * Load current metrics tab data
     */
    private async loadCurrentMetricsTab(): Promise<void> {
        this.loadingCurrentMetrics = true
        try {
            await this.loadCurrentMetrics()
            this.currentMetricsLoaded = true
        } catch (error) {
            console.error('[Reporting] Error loading current metrics:', error)
        } finally {
            this.loadingCurrentMetrics = false
        }
    }
    
    /**
     * Load all-time metrics tab data
     */
    private async loadAllTimeMetricsTab(): Promise<void> {
        this.loadingAllTimeMetrics = true
        try {
            await this.loadAllTimeMetrics()
            this.allTimeMetricsLoaded = true
        } catch (error) {
            console.error('[Reporting] Error loading all-time metrics:', error)
        } finally {
            this.loadingAllTimeMetrics = false
        }
    }

    updateUrlParams(): void {
        const queryParams: any = {}

        const source = this.filterForm.get('filterSource')?.value
        const campaign = this.filterForm.get('filterCampaign')?.value
        const dateRange = this.filterForm.get('filterDateRange')?.value

        if (source) queryParams.source = source
        if (campaign) queryParams.campaign = campaign
        if (dateRange && dateRange !== 'last30days') queryParams.dateRange = dateRange

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            replaceUrl: true,
        })
    }

    clearFilters(): void {
        this.filterForm.patchValue({
            filterDateRange: 'last30days',
            customStartDate: '',
            customEndDate: '',
            filterSource: '',
            filterCampaign: '',
        })
        this.filteredCampaigns = this.campaigns

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
        })
    }

    get isCustomDateRange(): boolean {
        return this.filterForm.get('filterDateRange')?.value === 'custom'
    }

    getDateRangeStart(dateRange: string): Date | null {
        const now = new Date()
        switch (dateRange) {
            case 'last7days':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            case 'last30days':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            case 'last3months':
                return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
            case 'last6months':
                return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
            case 'last12months':
                return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate())
            case 'thisyear':
                return new Date(now.getFullYear(), 0, 1)
            case 'custom':
                const customStart = this.filterForm.get('customStartDate')?.value
                return customStart ? new Date(customStart) : null
            case 'alltime':
            default:
                return null
        }
    }

    getDateRangeEnd(dateRange: string): Date | null {
        if (dateRange === 'custom') {
            const customEnd = this.filterForm.get('customEndDate')?.value
            if (customEnd) {
                const endDate = new Date(customEnd)
                endDate.setHours(23, 59, 59, 999)
                return endDate
            }
        }
        return null
    }

    getGoogleAnalyticsUrl(): string {
        const baseUrl = 'https://analytics.google.com/analytics/web/#/a226129700p311433464/reports/dashboard?r=reporting-hub'
        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)

        if (dateRangeStart) {
            const startDate = this.formatDateForGA(dateRangeStart)
            const endDate = dateRangeEnd ? this.formatDateForGA(dateRangeEnd) : this.formatDateForGA(new Date())
            return `${baseUrl}&params=_u.date00=${startDate}&_u.date01=${endDate}`
        }

        return baseUrl
    }

    formatDateForGA(date: Date): string {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}${month}${day}`
    }

    async loadReportingData() {
        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)
        const enterpriseKey = this.getFilterEnterpriseKey()

        // Build constraints array
        let constraints: any[] = []

        if (dateRangeStart && dateRangeEnd) {
            constraints = [where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc')]
        } else if (dateRangeStart) {
            constraints = [where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc')]
        } else {
            constraints = [orderBy('createdAt', 'desc')]
        }

        // Filter by enterprise key if selected (users have denormEnterpriseKey set on registration)
        if (enterpriseKey) {
            constraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
        }

        const clientsRef = query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS), ...constraints)
        const snapshot = await getDocs(clientsRef)
        let clients = snapshot.docs.map((doc) => {
            return new AdminClient({
                ...doc.data(),
                $key: doc.id,
            })
        })

        const filterSource = this.filterForm.get('filterSource')?.value
        const filterCampaign = this.filterForm.get('filterCampaign')?.value

        // When campaign is selected, filter by campaign only (campaign name is unique). Otherwise filter by source.
        if (filterCampaign) {
            clients = clients.filter((client) => client.p_campaign === filterCampaign)
        } else if (filterSource) {
            clients = clients.filter((client) => client.p_source === filterSource)
        }

        this.totalUsers = clients.length
        this.usersWithProfile = clients.filter((client) => client.firstName).length
        this.parkers = clients.filter((client) => client.hasEverHadActiveOrCompletedSubscription || client.hasActiveSubscription || client.hasFutureSubscription).length
    }

    async loadSubscriptionMetrics() {
        // Reset metrics
        this.totalSubscriptions = 0
        this.newSubscriptions = 0
        this.activeSubscriptions = 0
        this.completedSubscriptions = 0
        this.cancelledSubscriptions = 0
        this.breachedSubscriptions = 0
        this.rejectedSubscriptions = 0
        this.expiredSubscriptions = 0
        this.totalBaysBooked = 0
        this.newSubscriptionsBays = 0
        this.totalContractValue = 0
        this.newSubscriptionsTotalContractValue = 0
        this.newSubscriptionsMonthlyFees = 0
        this.newSubscriptionsMonths = 0
        this.totalMonthlyFees = 0
        this.totalMonths = 0
        this.averageSubscriptionValue = 0
        this.averageBaysPerSubscription = 0
        this.averageMonthlyBayPrice = 0
        this.averageMonthsDuration = 0

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)
        const enterpriseKey = this.getFilterEnterpriseKey()

        // Build query with filters
        let constraints: any[] = []

        if (dateRangeStart && dateRangeEnd) {
            constraints = [where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc')]
        } else if (dateRangeStart) {
            constraints = [where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc')]
        } else {
            constraints = [orderBy('createdAt', 'desc')]
        }

        // Add enterprise filter
        if (enterpriseKey) {
            constraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
        }

        const subscriptionsRef = query(collection(this.adminSvc.firestore, subscriptionsPath()), ...constraints)
        const snapshot = await getDocs(subscriptionsRef)

        let subscriptions = snapshot.docs.map((doc) => {
            return new Subscription({
                ...doc.data(),
                $key: doc.id,
            })
        })

        const filterSource = this.filterForm.get('filterSource')?.value
        const filterCampaign = this.filterForm.get('filterCampaign')?.value

        // When campaign is selected, filter by campaign only (campaign name is unique). Otherwise filter by source.
        if (filterCampaign) {
            subscriptions = subscriptions.filter((sub) => sub.p_campaign === filterCampaign)
        } else if (filterSource) {
            subscriptions = subscriptions.filter((sub) => sub.p_source === filterSource)
        }

        // Rejections and cancellations are counted before they're excluded from the value metrics below
        // (previously they were filtered out first, so these counters were always zero)
        subscriptions.forEach((subscription) => {
            if (subscription.status === ContractStatus.merchantRejected) this.rejectedSubscriptions++
            if (subscription.status === ContractStatus.clientCancelled) this.cancelledSubscriptions++
        })

        // Exclude subscriptions that never started (merchant rejected, client cancelled, payment failed)
        subscriptions = subscriptions.filter((sub) => !EXCLUDED_STATUSES_FROM_TRACKING.includes(sub.status))

        this.totalSubscriptions = subscriptions.length

        subscriptions.forEach((subscription) => {
            const isNewSubscription = subscription.status === ContractStatus.paymentInitiated || subscription.status === ContractStatus.active

            switch (subscription.status) {
                case ContractStatus.paymentInitiated:
                case ContractStatus.active:
                    this.newSubscriptions++
                    this.newSubscriptionsBays += subscription.baysBooked || 0
                    if (subscription.periodFee && subscription.period) {
                        this.newSubscriptionsTotalContractValue += (subscription.periodFee * subscription.period) / 100
                    }
                    if (subscription.periodFee) {
                        this.newSubscriptionsMonthlyFees += subscription.periodFee / 100
                    }
                    if (subscription.period) {
                        this.newSubscriptionsMonths += subscription.period
                    }
                    if (subscription.status === ContractStatus.active) this.activeSubscriptions++
                    break
                case ContractStatus.renewing:
                    this.activeSubscriptions++
                    break
                case ContractStatus.completed:
                    this.completedSubscriptions++
                    break
                case ContractStatus.mutuallyTerminated:
                    this.cancelledSubscriptions++
                    break
                case ContractStatus.expired:
                    this.expiredSubscriptions++
                    break
                case ContractStatus.clientBreachedNonPayment:
                case ContractStatus.merchantBreached:
                    this.breachedSubscriptions++
                    break
            }

            this.totalBaysBooked += subscription.baysBooked || 0
            if (subscription.periodFee && subscription.period) {
                this.totalContractValue += (subscription.periodFee * subscription.period) / 100
            }
            if (subscription.periodFee) {
                this.totalMonthlyFees += subscription.periodFee / 100
            }
            if (subscription.period) {
                this.totalMonths += subscription.period
            }
        })

        if (this.newSubscriptions > 0) {
            this.averageBaysPerSubscription = this.newSubscriptionsBays / this.newSubscriptions
            this.averageSubscriptionValue = this.newSubscriptionsTotalContractValue / this.newSubscriptions
            this.averageMonthsDuration = this.newSubscriptionsMonths / this.newSubscriptions
        }

        if (this.newSubscriptionsBays > 0) {
            this.averageMonthlyBayPrice = this.newSubscriptionsMonthlyFees / this.newSubscriptionsBays
        }
    }

    async loadInvoiceMetrics() {
        this.totalInvoicesAmount = 0
        this.totalInvoicesPaid = 0
        this.totalPaidOutAmount = 0

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)
        const enterpriseKey = this.getFilterEnterpriseKey()

        let constraints: any[] = []

        if (dateRangeStart && dateRangeEnd) {
            constraints = [where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc')]
        } else if (dateRangeStart) {
            constraints = [where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc')]
        } else {
            constraints = [orderBy('createdAt', 'desc')]
        }

        if (enterpriseKey) {
            constraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
        }

        const invoicesRef = query(collection(this.adminSvc.firestore, FirestoreStructure.INVOICES), ...constraints)
        const snapshot = await getDocs(invoicesRef)

        let invoices = snapshot.docs.map((doc) => {
            return new Invoice({
                ...doc.data(),
                $key: doc.id,
            })
        })

        const filterSource = this.filterForm.get('filterSource')?.value
        const filterCampaign = this.filterForm.get('filterCampaign')?.value

        // When campaign is selected, filter by campaign only (campaign name is unique). Otherwise filter by source.
        if (filterCampaign) {
            invoices = invoices.filter((invoice) => invoice.p_campaign === filterCampaign)
        } else if (filterSource) {
            invoices = invoices.filter((invoice) => invoice.p_source === filterSource)
        }

        invoices.forEach((invoice) => {
            if (!invoice.amountTotalDue) return
            // Cancelled and refunded invoices are not revenue
            if (invoice.status === InvoiceStatus.CANCELLED || invoice.status === InvoiceStatus.REFUNDED) return
            this.totalInvoicesAmount += invoice.amountTotalDue
            if (invoice.status === InvoiceStatus.PAID) {
                this.totalInvoicesPaid += invoice.amountTotalDue
                if (invoice.paidOut) this.totalPaidOutAmount += invoice.amountTotalDue
            }
        })
    }

    async loadPageViewMetrics() {
        this.pageViews = 0
        this.uniqueVisitors = 0

        const enterpriseKey = this.getFilterEnterpriseKey()
        
        // Enterprise Managers MUST have an enterprise filter - skip if not set yet
        if (this.enterpriseContext.isEnterpriseManager && !enterpriseKey) {
            console.log('[Reporting] Skipping page view metrics - Enterprise Manager without enterprise key')
            return
        }

        // Start with base query
        let pageViewsRef = query(collection(this.adminSvc.firestore, PAGE_VIEWS_COLLECTION))

        // Apply enterprise filter if selected
        if (enterpriseKey) {
            pageViewsRef = query(pageViewsRef, where('denormEnterpriseKey', '==', enterpriseKey))
        }

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)

        if (dateRangeStart) {
            if (dateRangeEnd) {
                pageViewsRef = query(pageViewsRef, where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc'))
            } else {
                pageViewsRef = query(pageViewsRef, where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc'))
            }
        } else {
            pageViewsRef = query(pageViewsRef, orderBy('createdAt', 'desc'))
        }

        try {
            const snapshot = await getDocs(pageViewsRef)
            let pageViewDocs = snapshot.docs.map((doc) => doc.data())

            const filterSource = this.filterForm.get('filterSource')?.value
            const filterCampaign = this.filterForm.get('filterCampaign')?.value

            // When campaign is selected, filter by campaign only (campaign name is unique). Otherwise filter by source.
            if (filterCampaign) {
                pageViewDocs = pageViewDocs.filter((pv: any) => pv.p_campaign === filterCampaign)
            } else if (filterSource) {
                pageViewDocs = pageViewDocs.filter((pv: any) => pv.p_source === filterSource)
            }

            this.pageViews = pageViewDocs.length

            const uniqueVisitorIds = new Set<string>()
            pageViewDocs.forEach((pv: any) => {
                if (pv.visitorId) {
                    uniqueVisitorIds.add(pv.visitorId)
                }
            })
            this.uniqueVisitors = uniqueVisitorIds.size
        } catch (error) {
            console.error('[Reporting] Error loading page view metrics:', error)
        }
    }

    async loadParkingMetrics() {
        this.newParkingBuildings = 0
        this.newParkingBays = 0

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)
        const enterpriseKey = this.getFilterEnterpriseKey()

        let constraints: any[] = []

        if (dateRangeStart && dateRangeEnd) {
            constraints = [where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc')]
        } else if (dateRangeStart) {
            constraints = [where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc')]
        } else {
            constraints = [orderBy('createdAt', 'desc')]
        }

        if (enterpriseKey) {
            constraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
        }

        try {
            const parkingsRef = query(collection(this.adminSvc.firestore, parkingsPath()), ...constraints)
            const snapshot = await getDocs(parkingsRef)

            const publishedParkings = snapshot.docs.filter((doc) => {
                const data = doc.data()
                return data['parkingStatus'] === ParkingStatus.PUBLISHED
            })

            this.newParkingBuildings = publishedParkings.length
            this.newParkingBays = publishedParkings.reduce((sum, doc) => {
                const data = doc.data()
                return sum + (Number(data['numberOfBays']) || 0)
            }, 0)
        } catch (error) {
            console.error('[Reporting] Error loading parking metrics:', error)
        }
    }

    formatNumber(value: number): string {
        if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M'
        } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K'
        }
        return value.toString()
    }

    formatCurrency(value: number): string {
        if (value >= 1000000) {
            return 'R' + (value / 1000000).toFixed(1) + 'M'
        } else if (value >= 1000) {
            return 'R' + (value / 1000).toFixed(0) + 'K'
        }
        return 'R' + value.toFixed(0)
    }

    openMoreDataModal() {
        this.showMoreDataModal = true
    }

    closeMoreDataModal() {
        this.showMoreDataModal = false
    }

    async loadCurrentMetrics() {
        this.currentActiveSubscriptions = 0
        this.currentActiveBays = 0
        this.currentMonthlyRevenue = 0
        this.currentDraftSubscriptions = 0
        this.currentPendingConfirmationSubscriptions = 0

        const activeStatuses = [ContractStatus.new, ContractStatus.paymentInitiated, ContractStatus.active, ContractStatus.bouncing, ContractStatus.renewing, ContractStatus.pendingClientApproval]
        const enterpriseKey = this.getFilterEnterpriseKey()

        try {
            let constraints: any[] = [where('status', 'in', activeStatuses)]
            if (enterpriseKey) {
                constraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }

            const activeRef = query(collection(this.adminSvc.firestore, subscriptionsPath()), ...constraints)
            const activeSnapshot = await getDocs(activeRef)

            let totalFee = 0
            activeSnapshot.docs.forEach((doc) => {
                const data = doc.data()
                const subscription = new Subscription(data, doc.id)

                this.currentActiveSubscriptions++
                this.currentActiveBays += subscription.baysBooked || 0

                try {
                    if (subscription.plan && subscription.plan.feeInCents && subscription.baysBooked) {
                        const fee = subscription.plan.feeInCents * subscription.baysBooked
                        if (!isNaN(fee)) {
                            totalFee += fee
                        }
                    }
                } catch (e) {
                    // Skip subscription fee calculation if it fails
                }
            })

            this.currentMonthlyRevenue = totalFee / 100

            // Draft subscriptions (use same filter key)
            let draftConstraints: any[] = [where('status', '==', ContractStatus.incomplete)]
            if (enterpriseKey) {
                draftConstraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }
            const draftRef = query(collection(this.adminSvc.firestore, subscriptionsPath()), ...draftConstraints)
            const draftSnapshot = await getDocs(draftRef)
            this.currentDraftSubscriptions = draftSnapshot.size

            this.currentPendingConfirmationSubscriptions = activeSnapshot.docs.filter((doc) => doc.data()['status'] === ContractStatus.pendingClientApproval).length
        } catch (error: any) {
            console.error('[Current Metrics] Error loading metrics:', error?.message || error)
        }
    }

    async loadAllTimeMetrics() {
        this.allTimeOverdueInvoices = 0
        this.allTimeOverdueAmount = 0
        this.allTimeNotPaidOut = 0
        this.allTimeNotPaidOutAmount = 0
        this.allTimeActiveBuildings = 0
        this.allTimeActiveParkingBays = 0
        this.allTimeTotalSubscriptions = 0
        this.allTimeTotalUsers = 0

        const enterpriseKey = this.getFilterEnterpriseKey()

        try {
            // Invoices with enterprise filter
            let invoiceConstraints: any[] = []
            if (enterpriseKey) {
                invoiceConstraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }
            const invoicesRef =
                invoiceConstraints.length > 0
                    ? query(collection(this.adminSvc.firestore, FirestoreStructure.INVOICES), ...invoiceConstraints)
                    : query(collection(this.adminSvc.firestore, FirestoreStructure.INVOICES))
            const invoicesSnapshot = await getDocs(invoicesRef)
            const invoices = invoicesSnapshot.docs.map((doc) => new Invoice({ ...doc.data(), $key: doc.id }))

            const now = new Date()
            invoices.forEach((invoice) => {
                if (invoice.status === InvoiceStatus.DUE && invoice.dueDate) {
                    let dueDate: Date
                    if (invoice.dueDate instanceof Date) {
                        dueDate = invoice.dueDate
                    } else if ((invoice.dueDate as any).toDate) {
                        dueDate = (invoice.dueDate as any).toDate()
                    } else {
                        dueDate = new Date((invoice.dueDate as any).seconds * 1000)
                    }
                    if (dueDate < now) {
                        this.allTimeOverdueInvoices++
                        this.allTimeOverdueAmount += invoice.amountTotalDue || 0
                    }
                }

                if (invoice.status === InvoiceStatus.PAID && !invoice.paidOut) {
                    this.allTimeNotPaidOut++
                    this.allTimeNotPaidOutAmount += invoice.periodFinalPayout || 0
                }
            })

            // Parkings with enterprise filter
            let parkingConstraints: any[] = []
            if (enterpriseKey) {
                parkingConstraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }
            const parkingsRef =
                parkingConstraints.length > 0 ? query(collection(this.adminSvc.firestore, parkingsPath()), ...parkingConstraints) : query(collection(this.adminSvc.firestore, parkingsPath()))
            const parkingsSnapshot = await getDocs(parkingsRef)

            const publishedParkings = parkingsSnapshot.docs.filter((doc) => {
                const data = doc.data()
                return data['parkingStatus'] === ParkingStatus.PUBLISHED
            })

            this.allTimeActiveBuildings = publishedParkings.length
            this.allTimeActiveParkingBays = publishedParkings.reduce((sum, doc) => {
                const data = doc.data()
                return sum + (Number(data['numberOfBays']) || 0)
            }, 0)

            // Subscriptions with enterprise filter
            let subConstraints: any[] = [where('status', 'in', [ContractStatus.active, ContractStatus.completed])]
            if (enterpriseKey) {
                subConstraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }
            const subscriptionsRef = query(collection(this.adminSvc.firestore, subscriptionsPath()), ...subConstraints)
            const subscriptionsSnapshot = await getDocs(subscriptionsRef)
            this.allTimeTotalSubscriptions = subscriptionsSnapshot.size

            // Users - filter by enterprise key if selected
            let userConstraints: any[] = []
            if (enterpriseKey) {
                userConstraints.push(where('denormEnterpriseKey', '==', enterpriseKey))
            }
            const usersRef =
                userConstraints.length > 0
                    ? query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS), ...userConstraints)
                    : query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS))
            const usersSnapshot = await getDocs(usersRef)
            this.allTimeTotalUsers = usersSnapshot.size
        } catch (error) {
            console.error('[Reporting] Error loading all-time metrics:', error)
        }
    }
}
